import { ReactNode, useMemo } from 'react';
import {
    TbBraces,
    TbBrandGoogleDrive,
    TbFileTypeCsv,
    TbFileTypeDocx,
    TbFileTypeJpg,
    TbFileTypePdf,
    TbFileTypePng,
    TbFileTypeTxt,
    TbFileTypeXls,
    TbFileTypeZip,
} from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { WorkspaceFolderGlyph } from '@/shared/components/workspace-resources';

export interface AttachmentFilePresentation {
    background: string;
    borderColor: string;
    icon: ReactNode;
    label: string;
}

export const formatAttachmentFileSize = (value?: string | number) => {
    const bytes = Number(value ?? 0);

    if (!Number.isFinite(bytes) || bytes <= 0) {
        return 'Unknown size';
    }

    if (bytes < 1024 * 1024) {
        return `${Math.max(Math.round(bytes / 1024), 1).toLocaleString()} KB`;
    }

    return `${(bytes / (1024 * 1024)).toLocaleString(undefined, { maximumFractionDigits: 1 })} MB`;
};

const useAttachmentFilePresentation = () => {
    const { token } = useAntDesignTheme();

    return useMemo<Record<string, AttachmentFilePresentation>>(() => {
        const folder: AttachmentFilePresentation = {
            background: token.colorBgBase,
            borderColor: token.colorBorder,
            icon: <WorkspaceFolderGlyph />,
            label: 'Folder',
        };
        return {
            'application/vnd.google-apps.folder': folder,
            'application/x-directory': folder,
            'application/json': {
                background: token.blue1,
                borderColor: token.blue3,
                icon: <TbBraces color={token.blue6} />,
                label: 'JSON',
            },
            'application/msword': {
                background: token.blue1,
                borderColor: token.blue3,
                icon: <TbFileTypeDocx color={token.blue6} />,
                label: 'Microsoft Word',
            },
            'application/pdf': {
                background: token.red1,
                borderColor: token.red3,
                icon: <TbFileTypePdf color={token.red6} />,
                label: 'PDF',
            },
            'application/vnd.google-apps.document': {
                background: token.blue1,
                borderColor: token.blue3,
                icon: <TbBrandGoogleDrive color={token.blue6} />,
                label: 'Google Docs',
            },
            'application/vnd.google-apps.form': {
                background: token.purple1,
                borderColor: token.purple3,
                icon: <TbBrandGoogleDrive color={token.purple6} />,
                label: 'Google Forms',
            },
            'application/vnd.google-apps.presentation': {
                background: token.orange1,
                borderColor: token.orange3,
                icon: <TbBrandGoogleDrive color={token.orange6} />,
                label: 'Google Slides',
            },
            'application/vnd.google-apps.spreadsheet': {
                background: token.green1,
                borderColor: token.green3,
                icon: <TbBrandGoogleDrive color={token.green6} />,
                label: 'Google Sheets',
            },
            'application/vnd.ms-excel': {
                background: token.green1,
                borderColor: token.green3,
                icon: <TbFileTypeXls color={token.green6} />,
                label: 'Microsoft Excel',
            },
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': {
                background: token.green1,
                borderColor: token.green3,
                icon: <TbFileTypeXls color={token.green6} />,
                label: 'Microsoft Excel',
            },
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': {
                background: token.blue1,
                borderColor: token.blue3,
                icon: <TbFileTypeDocx color={token.blue6} />,
                label: 'Microsoft Word',
            },
            'application/zip': {
                background: token.gold1,
                borderColor: token.gold3,
                icon: <TbFileTypeZip color={token.gold6} />,
                label: 'ZIP Archive',
            },
            'image/jpeg': {
                background: token.orange1,
                borderColor: token.orange3,
                icon: <TbFileTypeJpg color={token.orange6} />,
                label: 'JPEG Image',
            },
            'image/png': {
                background: token.magenta1,
                borderColor: token.magenta3,
                icon: <TbFileTypePng color={token.magenta6} />,
                label: 'PNG Image',
            },
            'text/csv': {
                background: token.green1,
                borderColor: token.green3,
                icon: <TbFileTypeCsv color={token.green6} />,
                label: 'CSV',
            },
            'text/plain': {
                background: token.colorBgBase,
                borderColor: token.colorBorder,
                icon: <TbFileTypeTxt color={token.colorTextSecondary} />,
                label: 'Plain Text',
            },
        };
    }, [token]);
};

export default useAttachmentFilePresentation;
